// TeacherCompleteStep.js - Final step for teacher onboarding
import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Animated } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from '../../i18n/useTranslation';

const TeacherCompleteStep = ({ userName, onGoToDashboard, onCreateFirstTest }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const firstName = userName?.split(' ')[0] || t('there');

  // Animation values
  const scaleAnim = useRef(new Animated.Value(0)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.sequence([
      Animated.spring(scaleAnim, {
        toValue: 1,
        tension: 50,
        friction: 7,
        useNativeDriver: true,
      }),
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 400,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  const colors = {
    bg: isDark ? '#1e293b' : '#f8fafc',
    card: isDark ? '#334155' : '#ffffff',
    text: isDark ? '#f8fafc' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    accent: '#6366f1',
    accentLight: isDark ? '#6366f130' : '#6366f115',
    border: isDark ? '#475569' : '#e2e8f0',
    success: '#10b981',
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.bg }]}>
      <View style={[styles.content, { backgroundColor: colors.card, borderColor: colors.border }]}>
        {/* Celebration icon with animation */}
        <Animated.View
          style={[
            styles.celebrationContainer,
            { transform: [{ scale: scaleAnim }] },
          ]}
        >
          <View style={[styles.iconContainer, { backgroundColor: colors.success + '20' }]}>
            <MaterialCommunityIcons name="check-decagram" size={56} color={colors.success} />
          </View>
        </Animated.View>

        {/* Title */}
        <Text style={[styles.title, { color: colors.text }]}>
          {t("You're Ready to Go!")}
        </Text>

        {/* Personalized message */}
        <Animated.View style={{ opacity: fadeAnim, width: '100%' }}>
          <Text style={[styles.message, { color: colors.textSecondary }]}>
            {t('Welcome aboard, {name}! Your teacher dashboard is set up and ready.').replace('{name}', firstName)}
          </Text>

          {/* Quick start options */}
          <View style={[styles.optionsBox, { backgroundColor: colors.bg, borderColor: colors.border }]}>
            <Text style={[styles.optionsTitle, { color: colors.text }]}>
              {t('What would you like to do first?')}
            </Text>

            <TouchableOpacity
              style={[styles.optionCard, { borderColor: colors.accent, backgroundColor: colors.accentLight }]}
              onPress={onCreateFirstTest}
              activeOpacity={0.7}
            >
              <MaterialCommunityIcons name="clipboard-plus-outline" size={24} color={colors.accent} />
              <View style={styles.optionText}>
                <Text style={[styles.optionTitle, { color: colors.accent }]}>{t('Create Your First Test')}</Text>
                <Text style={[styles.optionDescription, { color: colors.textSecondary }]}>
                  {t('Get started by creating an assessment')}
                </Text>
              </View>
              <MaterialCommunityIcons name="chevron-right" size={20} color={colors.accent} />
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.optionCard, { borderColor: colors.border }]}
              onPress={onGoToDashboard}
              activeOpacity={0.7}
            >
              <MaterialCommunityIcons name="view-dashboard-outline" size={24} color={colors.textSecondary} />
              <View style={styles.optionText}>
                <Text style={[styles.optionTitle, { color: colors.text }]}>{t('Explore Dashboard')}</Text>
                <Text style={[styles.optionDescription, { color: colors.textSecondary }]}>
                  {t('Browse your dashboard and settings')}
                </Text>
              </View>
              <MaterialCommunityIcons name="chevron-right" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>
        </Animated.View>

        {/* Go to Dashboard Button */}
        <Animated.View style={{ opacity: fadeAnim, width: '100%' }}>
          <TouchableOpacity
            style={[styles.primaryButton, { backgroundColor: colors.accent }]}
            onPress={onGoToDashboard}
            activeOpacity={0.8}
          >
            <MaterialCommunityIcons name="arrow-right" size={20} color="#fff" />
            <Text style={styles.primaryButtonText}>{t('Go to Dashboard')}</Text>
          </TouchableOpacity>
        </Animated.View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  content: {
    width: '100%',
    maxWidth: 480,
    borderRadius: 16,
    padding: 32,
    alignItems: 'center',
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
  },
  celebrationContainer: {
    marginBottom: 24,
  },
  iconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 12,
  },
  message: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 24,
    paddingHorizontal: 8,
  },
  optionsBox: {
    width: '100%',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 24,
  },
  optionsTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 12,
  },
  optionCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    marginBottom: 8,
    gap: 12,
  },
  optionText: {
    flex: 1,
  },
  optionTitle: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 2,
  },
  optionDescription: {
    fontSize: 12,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 10,
    width: '100%',
    gap: 8,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default TeacherCompleteStep;
